/**
 * Anlegen und Bearbeiten eines Geschaeftspartners.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { formatAmount, parseAmount } from '@erp/shared';

import { ApiError, api } from '../api/client';
import type { Partner, PaymentTerm } from '../api/types';
import { useAuth } from '../auth';
import { PageHeader } from '../components/Layout';
import { PartnerActivities } from '../components/PartnerActivities';
import { PartnerAddresses } from '../components/PartnerAddresses';
import { PartnerBankAccounts } from '../components/PartnerBankAccounts';
import { PartnerContacts } from '../components/PartnerContacts';
import { PartnerDocuments } from '../components/PartnerDocuments';
import { PartnerRecords } from '../components/PartnerRecords';
import { useToast } from '../components/toast';
import { confirmLeave, useUnsavedChanges } from '../components/useUnsavedChanges';
import { UserSelect } from '../components/UserSelect';
import {
  Alert,
  Checkbox,
  Dialog,
  Field,
  LoadingBlock,
  NumericInput,
  Select,
  TextArea,
  TextInput,
} from '../components/ui';

type PartnerForm = {
  number: string;
  name: string;
  name2: string;
  partnerType: 'company' | 'person';
  isCustomer: boolean;
  isSupplier: boolean;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  phone: string;
  email: string;
  invoiceEmail: string;
  website: string;
  vatId: string;
  taxNumber: string;
  paymentTermId: string;
  defaultDiscountPercent: number;
  creditLimit: number;
  crefoId: string;
  accountsReceivable: string;
  accountsPayable: string;
  routingId: string;
  invoiceFormat: 'pdf' | 'zugferd' | 'xrechnung';
  salesRepId: string;
  notes: string;
  active: boolean;
};

function emptyForm(role: 'customer' | 'supplier'): PartnerForm {
  return {
    number: '',
    name: '',
    name2: '',
    partnerType: 'company',
    isCustomer: role === 'customer',
    isSupplier: role === 'supplier',
    street: '',
    postalCode: '',
    city: '',
    country: 'DE',
    phone: '',
    email: '',
    invoiceEmail: '',
    website: '',
    vatId: '',
    taxNumber: '',
    paymentTermId: '',
    defaultDiscountPercent: 0,
    creditLimit: 0,
    crefoId: '',
    accountsReceivable: '',
    accountsPayable: '',
    routingId: '',
    invoiceFormat: 'pdf',
    salesRepId: '',
    notes: '',
    active: true,
  };
}

function toForm(partner: Partner): PartnerForm {
  return {
    number: partner.number,
    name: partner.name,
    name2: partner.name2 ?? '',
    partnerType: partner.partnerType,
    isCustomer: partner.isCustomer,
    isSupplier: partner.isSupplier,
    street: partner.street ?? '',
    postalCode: partner.postalCode ?? '',
    city: partner.city ?? '',
    country: partner.country,
    phone: partner.phone ?? '',
    email: partner.email ?? '',
    invoiceEmail: partner.invoiceEmail ?? '',
    website: partner.website ?? '',
    vatId: partner.vatId ?? '',
    taxNumber: partner.taxNumber ?? '',
    paymentTermId: partner.paymentTermId ?? '',
    defaultDiscountPercent: partner.defaultDiscountPercent,
    creditLimit: partner.creditLimit,
    crefoId: partner.crefoId ?? '',
    accountsReceivable: partner.accountsReceivable ?? '',
    accountsPayable: partner.accountsPayable ?? '',
    routingId: partner.routingId ?? '',
    invoiceFormat: partner.invoiceFormat,
    salesRepId: partner.salesRepId ?? '',
    notes: partner.notes ?? '',
    active: partner.active,
  };
}

export function PartnerDetailPage({ role }: { role: 'customer' | 'supplier' }) {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'neu' || id === undefined;

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can, moduleEnabled } = useAuth();

  const basePath = role === 'customer' ? '/kunden' : '/lieferanten';

  const [form, setForm] = useState<PartnerForm | null>(isNew ? emptyForm(role) : null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  /** Sperrdialog: 'lock' fragt den Grund ab, 'unlock' bestaetigt das Aufheben. */
  const [lockDialog, setLockDialog] = useState<'lock' | 'unlock' | null>(null);
  const [lockReason, setLockReason] = useState('');

  const { data: paymentTerms } = useQuery({
    queryKey: ['payment-terms'],
    queryFn: () => api.get<{ paymentTerms: PaymentTerm[] }>('/api/settings/payment-terms'),
  });

  // Vorschlag fuer die naechste freie Nummer beim Neuanlegen.
  useQuery({
    queryKey: ['partner-next-number', role],
    queryFn: async () => {
      const result = await api.get<{ number: string }>(`/api/partners/next-number?role=${role}`);
      setForm((current) => (current && !current.number ? { ...current, number: result.number } : current));
      return result;
    },
    enabled: isNew,
  });

  const { data: loaded, isLoading } = useQuery({
    queryKey: ['partner', id],
    queryFn: () => api.get<{ partner: Partner }>(`/api/partners/${id}`),
    enabled: !isNew,
  });
  // Formular aus den Daten fuellen, nicht in der Ladefunktion: Kommt der
  // Partner aus dem Zwischenspeicher, laeuft die Ladefunktion nicht.
  useEffect(() => {
    if (!isNew && loaded) setForm(toForm(loaded.partner));
  }, [loaded, isNew]);

  const save = useMutation({
    mutationFn: async (values: PartnerForm) => {
      const payload = {
        ...values,
        name2: values.name2 || null,
        street: values.street || null,
        postalCode: values.postalCode || null,
        city: values.city || null,
        phone: values.phone || null,
        email: values.email || null,
        invoiceEmail: values.invoiceEmail || null,
        website: values.website || null,
        vatId: values.vatId || null,
        taxNumber: values.taxNumber || null,
        crefoId: values.crefoId || null,
        paymentTermId: values.paymentTermId || null,
        accountsReceivable: values.accountsReceivable || null,
        accountsPayable: values.accountsPayable || null,
        routingId: values.routingId || null,
        salesRepId: values.salesRepId || null,
        notes: values.notes || null,
      };

      return isNew
        ? api.post<{ partner: Partner }>('/api/partners', payload)
        : api.patch<{ partner: Partner }>(`/api/partners/${id}`, payload);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      queryClient.invalidateQueries({ queryKey: ['partner', id] });
      toast.success('Gespeichert.');
      if (isNew) navigate(`${basePath}/${result.partner.id}`, { replace: true });
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setErrors(error.fieldErrors);
        toast.error(error.message);
      } else {
        toast.error('Beim Speichern ist ein Fehler aufgetreten.');
      }
    },
  });

  const remove = useMutation({
    mutationFn: () =>
      api.delete<{ deleted: boolean; deactivated: boolean; message?: string }>(`/api/partners/${id}`),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      setConfirmDelete(false);
      if (result.deleted) {
        toast.success('Der Datensatz wurde gelöscht.');
        navigate(basePath);
      } else {
        toast.info(result.message ?? 'Der Partner wurde deaktiviert.');
        queryClient.invalidateQueries({ queryKey: ['partner', id] });
      }
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : 'Löschen fehlgeschlagen.'),
  });

  const toggleLock = useMutation({
    mutationFn: (mode: 'lock' | 'unlock') =>
      api.post<{ partner: Partner }>(
        `/api/partners/${id}/${mode}`,
        mode === 'lock' ? { reason: lockReason } : { note: lockReason || null },
      ),
    onSuccess: (_result, mode) => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      queryClient.invalidateQueries({ queryKey: ['partner', id] });
      setLockDialog(null);
      setLockReason('');
      toast.success(mode === 'lock' ? 'Der Datensatz ist gesperrt.' : 'Die Sperre ist aufgehoben.');
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : 'Die Aktion ist fehlgeschlagen.'),
  });

  // Verlassen-Warnung: Formular gegen den gespeicherten Stand halten. Bei
  // einem neuen Datensatz zaehlt die vorgeschlagene Nummer nicht als Eingabe.
  // (Vor den fruehen Rueckgaben, weil ein Hook immer laufen muss.)
  const baseline = !form ? null : isNew ? { ...emptyForm(role), number: form.number } : loaded ? toForm(loaded.partner) : null;
  const dirty =
    can('masterdata:write') && form !== null && baseline !== null && JSON.stringify(form) !== JSON.stringify(baseline);
  useUnsavedChanges(dirty);

  if (!isNew && isLoading) return <LoadingBlock />;
  if (!form) return <LoadingBlock />;

  // Satzsperre: Gesperrte Datensaetze aendert nur, wer sperren darf.
  const locked = Boolean(loaded?.partner.lockedAt);
  const mayLock = can('masterdata:lock');
  const readOnly = !can('masterdata:write') || (locked && !mayLock);

  function update<K extends keyof PartnerForm>(key: K, value: PartnerForm[K]) {
    setForm((current) => (current ? { ...current, [key]: value } : current));
    setErrors((current) => {
      if (!current[key as string]) return current;
      const next = { ...current };
      delete next[key as string];
      return next;
    });
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setErrors({});
    save.mutate(form!);
  }

  const title = isNew
    ? role === 'customer'
      ? 'Neuer Kunde'
      : 'Neuer Lieferant'
    : `${form.number} · ${form.name}`;

  return (
    <form onSubmit={handleSubmit}>
      <PageHeader
        title={title}
        subtitle={
          !isNew && loaded
            ? `Angelegt am ${new Date(loaded.partner.createdAt).toLocaleDateString('de-DE')}`
            : undefined
        }
        actions={
          <>
            <button type="button" className="btn" onClick={() => confirmLeave(dirty) && navigate(basePath)}>
              Zurück
            </button>
            {!isNew && id && can('reports:read') ? (
              <button
                type="button"
                className="btn"
                title="Welche Artikel hat dieser Partner wann bezogen bzw. geliefert?"
                onClick={() => navigate(`/auswertungen/berichte?report=item-history&partnerId=${id}`)}
              >
                Artikelhistorie
              </button>
            ) : null}
            {!isNew && id && mayLock ? (
              <button
                type="button"
                className="btn"
                title={locked ? 'Satzsperre aufheben' : 'Satzsperre setzen: keine Belege, keine Änderungen'}
                onClick={() => {
                  setLockReason('');
                  setLockDialog(locked ? 'unlock' : 'lock');
                }}
              >
                {locked ? 'Entsperren' : 'Sperren'}
              </button>
            ) : null}
            {!isNew && !readOnly ? (
              <button type="button" className="btn btn-danger" onClick={() => setConfirmDelete(true)}>
                Löschen
              </button>
            ) : null}
            {!readOnly ? (
              <button type="submit" className="btn btn-primary" disabled={save.isPending}>
                {save.isPending ? 'Wird gespeichert …' : 'Speichern'}
              </button>
            ) : null}
          </>
        }
      />

      {locked && loaded ? (
        <Alert kind="error">
          <strong>Gesperrt</strong> seit {new Date(loaded.partner.lockedAt!).toLocaleDateString('de-DE')}
          {loaded.partner.lockedByName ? ` von ${loaded.partner.lockedByName}` : ''}
          {loaded.partner.lockReason ? ` – ${loaded.partner.lockReason}` : ''}. Für diesen Partner werden keine
          Belege angelegt oder festgeschrieben.
          {mayLock ? ' Änderungen sind Ihnen mit dem Recht „Sperren" weiterhin möglich.' : ' Änderungen sind nur mit dem Recht „Sperren" möglich.'}
        </Alert>
      ) : null}

      {readOnly && !locked ? (
        <Alert kind="info">Sie können diese Daten ansehen, aber nicht ändern.</Alert>
      ) : null}

      {!form.active ? <Alert kind="warning">Dieser Datensatz ist deaktiviert.</Alert> : null}

      {lockDialog ? (
        <Dialog
          title={lockDialog === 'lock' ? 'Datensatz sperren?' : 'Sperre aufheben?'}
          onClose={() => setLockDialog(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setLockDialog(null)}>
                Abbrechen
              </button>
              <button
                type="button"
                className={`btn ${lockDialog === 'lock' ? 'btn-danger' : 'btn-primary'}`}
                disabled={toggleLock.isPending || (lockDialog === 'lock' && !lockReason.trim())}
                onClick={() => toggleLock.mutate(lockDialog)}
              >
                {lockDialog === 'lock' ? 'Sperren' : 'Entsperren'}
              </button>
            </>
          }
        >
          <p style={{ marginTop: 0 }}>
            {lockDialog === 'lock' ? (
              <>
                <strong>{form.name}</strong> wird gesperrt: Niemand kann Belege für diesen Partner anlegen oder
                festschreiben, Stammdaten ändern nur Benutzer mit dem Recht „Sperren". Storno und Zahlungen
                bleiben möglich.
              </>
            ) : (
              <>
                Die Sperre von <strong>{form.name}</strong> wird aufgehoben. Belege und Änderungen sind danach
                wieder möglich.
              </>
            )}
          </p>
          <Field label={lockDialog === 'lock' ? 'Sperrgrund' : 'Bemerkung (optional)'} required={lockDialog === 'lock'} span={12}>
            <TextArea
              rows={3}
              value={lockReason}
              onChange={setLockReason}
              placeholder={lockDialog === 'lock' ? 'z. B. Zahlungsrückstand, Insolvenz, Streitfall' : ''}
            />
          </Field>
        </Dialog>
      ) : null}

      <div className="card">
        <div className="card-body">
          <fieldset>
            <legend>Grunddaten</legend>
            <div className="form-grid">
              <Field label="Nummer" span={3} required error={errors.number}>
                <TextInput
                  value={form.number}
                  onChange={(value) => update('number', value)}
                  invalid={Boolean(errors.number)}
                  disabled={readOnly}
                />
              </Field>

              <Field label="Art" span={3}>
                <Select
                  value={form.partnerType}
                  onChange={(value) => update('partnerType', value)}
                  options={[
                    { value: 'company', label: 'Firma' },
                    { value: 'person', label: 'Privatperson' },
                  ]}
                  disabled={readOnly}
                />
              </Field>

              <Field label="Rolle" span={6} error={errors.isCustomer}>
                <div className="row" style={{ gap: 20, paddingTop: 5 }}>
                  <Checkbox
                    id="isCustomer"
                    checked={form.isCustomer}
                    onChange={(value) => update('isCustomer', value)}
                    label="Kunde"
                    disabled={readOnly}
                  />
                  <Checkbox
                    id="isSupplier"
                    checked={form.isSupplier}
                    onChange={(value) => update('isSupplier', value)}
                    label="Lieferant"
                    disabled={readOnly}
                  />
                </div>
              </Field>

              <Field label="Name" span={6} required error={errors.name}>
                <TextInput
                  value={form.name}
                  onChange={(value) => update('name', value)}
                  invalid={Boolean(errors.name)}
                  disabled={readOnly}
                />
              </Field>

              <Field label="Namenszusatz" span={6} error={errors.name2}>
                <TextInput
                  value={form.name2}
                  onChange={(value) => update('name2', value)}
                  disabled={readOnly}
                />
              </Field>
            </div>
          </fieldset>

          <fieldset>
            <legend>Anschrift</legend>
            <div className="form-grid">
              <Field label="Straße und Hausnummer" span={6} error={errors.street}>
                <TextInput
                  value={form.street}
                  onChange={(value) => update('street', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="PLZ" span={2} error={errors.postalCode}>
                <TextInput
                  value={form.postalCode}
                  onChange={(value) => update('postalCode', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Ort" span={3} error={errors.city}>
                <TextInput
                  value={form.city}
                  onChange={(value) => update('city', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Land" span={1} error={errors.country} hint="ISO">
                <TextInput
                  value={form.country}
                  onChange={(value) => update('country', value.toUpperCase())}
                  maxLength={2}
                  disabled={readOnly}
                />
              </Field>
            </div>
          </fieldset>

          <fieldset>
            <legend>Kontakt</legend>
            <div className="form-grid">
              <Field label="Telefon" span={3} error={errors.phone}>
                <TextInput
                  value={form.phone}
                  onChange={(value) => update('phone', value)}
                  type="tel"
                  disabled={readOnly}
                />
              </Field>
              <Field label="E-Mail" span={4} error={errors.email}>
                <TextInput
                  value={form.email}
                  onChange={(value) => update('email', value)}
                  type="email"
                  invalid={Boolean(errors.email)}
                  disabled={readOnly}
                />
              </Field>
              <Field
                label="E-Mail für Rechnungen"
                span={5}
                error={errors.invoiceEmail}
                hint="Leer lassen, wenn die allgemeine Adresse gilt"
              >
                <TextInput
                  value={form.invoiceEmail}
                  onChange={(value) => update('invoiceEmail', value)}
                  type="email"
                  invalid={Boolean(errors.invoiceEmail)}
                  disabled={readOnly}
                />
              </Field>
            </div>
          </fieldset>

          <fieldset>
            <legend>Steuer und Konditionen</legend>
            <div className="form-grid">
              <Field
                label="USt-IdNr."
                span={3}
                error={errors.vatId}
                hint="Pflicht bei EU-Lieferungen"
              >
                <TextInput
                  value={form.vatId}
                  onChange={(value) => update('vatId', value.toUpperCase())}
                  invalid={Boolean(errors.vatId)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Steuernummer" span={3} error={errors.taxNumber}>
                <TextInput
                  value={form.taxNumber}
                  onChange={(value) => update('taxNumber', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field label="Zahlungsbedingung" span={6} error={errors.paymentTermId}>
                <Select
                  value={form.paymentTermId}
                  onChange={(value) => update('paymentTermId', value)}
                  placeholder="— Vorgabe aus den Einstellungen —"
                  options={(paymentTerms?.paymentTerms ?? []).map((term) => ({
                    value: term.id,
                    label: term.name,
                  }))}
                  disabled={readOnly}
                />
              </Field>

              {form.isCustomer ? (
                <Field
                  label="Zuständiger Außendienst"
                  span={6}
                  error={errors.salesRepId}
                  hint="Belegt neue Belege dieses Kunden vor — Grundlage der Auswertung je Mitarbeiter"
                >
                  <UserSelect
                    value={form.salesRepId}
                    onChange={(value) => update('salesRepId', value)}
                    emptyLabel="Kein fester Außendienst"
                    disabled={readOnly}
                  />
                </Field>
              ) : null}

              <Field label="Rabatt in %" span={3} error={errors.defaultDiscountPercent}>
                <PercentInput
                  value={form.defaultDiscountPercent}
                  onChange={(value) => update('defaultDiscountPercent', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field
                label="Kreditlimit"
                span={3}
                error={errors.creditLimit}
                hint="0 = keine Prüfung beim Festschreiben von Aufträgen"
              >
                <NumericInput
                  kind="amount"
                  value={form.creditLimit}
                  onChange={(value) => update('creditLimit', value)}
                  disabled={readOnly}
                />
              </Field>
              <Field
                label="Crefo-Nummer"
                span={3}
                error={errors.crefoId}
                hint="Creditreform-Kennung für Auskünfte"
              >
                <TextInput
                  value={form.crefoId}
                  onChange={(value) => update('crefoId', value)}
                  disabled={readOnly}
                />
              </Field>
              {form.isCustomer ? (
                <Field label="Debitorenkonto" span={3} error={errors.accountsReceivable}>
                  <TextInput
                    value={form.accountsReceivable}
                    onChange={(value) => update('accountsReceivable', value)}
                    disabled={readOnly}
                  />
                </Field>
              ) : null}
              {form.isSupplier ? (
                <Field label="Kreditorenkonto" span={3} error={errors.accountsPayable}>
                  <TextInput
                    value={form.accountsPayable}
                    onChange={(value) => update('accountsPayable', value)}
                    disabled={readOnly}
                  />
                </Field>
              ) : null}
            </div>
          </fieldset>

          {form.isCustomer ? (
            <fieldset>
              <legend>Rechnungsversand</legend>
              <div className="form-grid">
                <Field
                  label="Format"
                  span={4}
                  hint="Ab 2025 muss jedes Unternehmen E-Rechnungen empfangen können"
                >
                  <Select
                    value={form.invoiceFormat}
                    onChange={(value) => update('invoiceFormat', value)}
                    options={[
                      { value: 'pdf', label: 'PDF (klassisch)' },
                      { value: 'zugferd', label: 'ZUGFeRD (PDF mit XML)' },
                      { value: 'xrechnung', label: 'XRechnung (reines XML)' },
                    ]}
                    disabled={readOnly}
                  />
                </Field>
                <Field
                  label="Leitweg-ID"
                  span={4}
                  error={errors.routingId}
                  hint="Nur bei öffentlichen Auftraggebern"
                >
                  <TextInput
                    value={form.routingId}
                    onChange={(value) => update('routingId', value)}
                    disabled={readOnly}
                  />
                </Field>
              </div>
            </fieldset>
          ) : null}

          <fieldset style={{ marginBottom: 0 }}>
            <legend>Notizen</legend>
            <TextArea
              value={form.notes}
              onChange={(value) => update('notes', value)}
              rows={3}
              disabled={readOnly}
            />
          </fieldset>
        </div>
      </div>

      {/* Anschriften gibt es erst, wenn der Partner gespeichert ist. */}
      {!isNew ? <PartnerContacts partnerId={id!} /> : null}

      {!isNew ? <PartnerAddresses partnerId={id!} /> : null}

      {!isNew && can('accounting:read') ? <PartnerBankAccounts partnerId={id!} /> : null}

      {!isNew ? <PartnerDocuments partnerId={id!} /> : null}

      {!isNew && moduleEnabled('crm') && can('crm:read') ? (
        <PartnerActivities
          partnerId={id!}
          partnerLabel={`${form.number} · ${form.name}`}
          salesRepId={form.salesRepId || null}
        />
      ) : null}

      {!isNew ? <PartnerRecords partnerId={id!} role={role} creditLimit={form.creditLimit} /> : null}

      {confirmDelete ? (
        <Dialog
          title="Datensatz löschen?"
          onClose={() => setConfirmDelete(false)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setConfirmDelete(false)}>
                Abbrechen
              </button>
              <button
                type="button"
                className="btn btn-danger"
                disabled={remove.isPending}
                onClick={() => remove.mutate()}
              >
                Löschen
              </button>
            </>
          }
        >
          <p style={{ margin: 0 }}>
            <strong>{form.name}</strong> wird gelöscht. Sind bereits Belege vorhanden, wird der
            Partner stattdessen deaktiviert — Belege müssen zehn Jahre lang lesbar bleiben.
          </p>
        </Dialog>
      ) : null}
    </form>
  );
}

/** Prozentfeld auf Basispunkten (500 = 5,00 %). */
function PercentInput({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}) {
  // Basispunkte und Cent haben beide zwei Nachkommastellen - die
  // Umrechnung ist daher dieselbe wie bei Betraegen.
  return (
    <input autoComplete="off"
      className="input numeric"
      type="text"
      inputMode="decimal"
      defaultValue={formatAmount(value)}
      disabled={disabled}
      onFocus={(event) => event.target.select()}
      onBlur={(event) => {
        const parsed = parseAmount(event.target.value);
        const next = parsed === null ? 0 : Math.min(10_000, Math.max(0, parsed));
        event.target.value = formatAmount(next);
        onChange(next);
      }}
    />
  );
}
